import { useState, useRef } from 'react';

function ImageAnalyzer({ token }) {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setDescription('');
      setDetails(null);
      setError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
      setDescription('');
      setDetails(null);
      setError('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const analyzeImage = async () => {
    if (!image) return;

    setLoading(true);
    setError('');
    setDescription('');
    setDetails(null);

    const formData = new FormData();
    formData.append('image', image);

    try {
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al analizar la imagen');
      }

      setDescription(data.description);
      setDetails(data.details);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setImage(null);
    setPreview(null);
    setDescription('');
    setDetails(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="analyzer-page">
      <h2 className="page-title">Analizar Imagen</h2>
      <p className="page-subtitle">Sube una imagen y watsonx.ai la describirá en detalle</p>

      <div
        className={`upload-zone ${preview ? 'has-image' : ''}`}
        onClick={() => !preview && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {preview ? (
          <img src={preview} alt="Preview" className="image-preview" />
        ) : (
          <div className="upload-placeholder">
            <div className="upload-icon">+</div>
            <p>Arrastra una imagen aquí o haz clic para seleccionar</p>
            <span className="upload-hint">JPEG, PNG, GIF o WebP (max 10MB)</span>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageChange}
          accept="image/jpeg,image/png,image/gif,image/webp"
          hidden
        />
      </div>

      <div className="actions">
        {preview && (
          <>
            <button className="btn btn-primary" onClick={analyzeImage} disabled={loading}>
              {loading ? 'Analizando...' : 'Analizar Imagen'}
            </button>
            <button className="btn btn-secondary" onClick={reset} disabled={loading}>
              Nueva Imagen
            </button>
          </>
        )}
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Analizando imagen con watsonx.ai...</p>
        </div>
      )}

      {error && (
        <div className="error-box">
          <strong>Error:</strong> {error}
        </div>
      )}

      {description && (
        <div className="result-box">
          <h2>Descripción de la imagen</h2>
          <p className="description-text">{description}</p>
        </div>
      )}

      {details?.usage && (
        <div className="token-box">
          <h3>Consumo de tokens</h3>
          <div className="token-grid">
            <div className="token-item">
              <span className="token-value">{details.usage.prompt_tokens.toLocaleString()}</span>
              <span className="token-label">Prompt</span>
            </div>
            <div className="token-item">
              <span className="token-value">{details.usage.completion_tokens.toLocaleString()}</span>
              <span className="token-label">Respuesta</span>
            </div>
            <div className="token-item total">
              <span className="token-value">{details.usage.total_tokens.toLocaleString()}</span>
              <span className="token-label">Total</span>
            </div>
          </div>
          <p className="token-model">Modelo: {details.model}</p>
        </div>
      )}
    </div>
  );
}

export default ImageAnalyzer;
